import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import {
  actions as ordersActions,
  selectors as ordersSelectors,
} from 'state/orders';

import { formatters as dateAndTimeFormatters } from 'utils/dateAndTimes';
import { formatters as addressesFormatters } from 'utils/addresses';
import { getAppointmentDate } from 'utils/order';
import { isEmpty } from 'utils/types';

import ShipmentCard from 'components/orders/ShipmentCard/ShipmentCard';
import { IS_TENANT } from 'tenant/utils';
const DEFAULT_IGNORED_STOPS_DURING_OPERATIVE = 1;

const enhance = compose(
  withTranslation('ShipmentCard'),
  connect(
    (state, { orderId }) => ({
      order: ordersSelectors.getOrderById(state, orderId),
      shipmentsRefs: ordersSelectors.getInternalReferences(state, orderId),
    }),
    {
      openOrder: ordersActions.openOrder,
    }
  ),
  withProps(({ type, t, order, shipmentsRefs }) => {
    const {
      orderType,
      pickupCompany,
      dropoffCompany,
      pickupLabel,
      dropoffLabel,
      stops,
    } = destructureOrderParams(order);
    const { shipments = [] } = order;
    const lastShipment = shipments[shipments?.length - 1];
    const rawRef =
      shipmentsRefs?.length === 1 ? shipmentsRefs[0] : t('multiple_ref');
    const datePickup = {
      valueFrom: order?.start_time_from,
      valueTo: order?.start_time_to,
      timezone: order?.start_time_from_tz,
      t,
    };
    const dateDropoff = {
      valueFrom: order?.end_time_from,
      valueTo: order?.end_time_to,
      timezone: order?.end_time_from_tz,
      t,
    };

    const ignoredStopsDuringOperativeConfig = IS_TENANT
      ? 2
      : DEFAULT_IGNORED_STOPS_DURING_OPERATIVE;

    const remainingStopsInMultiroute =
      stops?.length - ignoredStopsDuringOperativeConfig;

    const driver = order?.drivers?.length > 0 ? order.drivers[0] : null;

    return {
      orderRef: !isEmpty(rawRef) ? ` · ${rawRef}` : rawRef,
      datePickup:
        dateAndTimeFormatters.getHumanReadableFormattedDateAndTimes(datePickup),
      dateDropoff:
        dateAndTimeFormatters.getHumanReadableFormattedDateAndTimes(
          dateDropoff
        ),
      dataPickup:
        orderType === 'COLLECTION'
          ? `${remainingStopsInMultiroute} ${t('label.pickup')}`
          : pickupCompany,
      dateAppointment: getAppointmentDate({
        t,
        stopComlpeted: false,
        appointment: lastShipment?.dropoff_appointment,
        timezone: order?.end_time_from_tz,
      }),
      locationPickup:
        orderType === 'COLLECTION'
          ? addressesFormatters.formatCompaniesByStopType(stops, 'pickup', t)
          : pickupLabel,
      dataDropoff:
        orderType === 'DISTRIBUTION'
          ? `${remainingStopsInMultiroute} ${t('label.dropoff')}`
          : dropoffCompany,
      locationDropoff:
        orderType === 'DISTRIBUTION'
          ? addressesFormatters.formatCompaniesByStopType(stops, 'dropoff', t)
          : dropoffLabel,
      cargoManifests: getCargoManifestsUrls(stops),
      numberOfStops: stops?.length,
      isRecurring: order?.recurring_order,
      type,
      driver,
      pendingAppointment:
        !!lastShipment?.dropoff_appointment &&
        !lastShipment?.dropoff_appointment?.time_from,
    };
  })
);

export function getCargoManifestsUrls(stops) {
  return (stops || [])
    .reduce(
      (acc, stop) => [
        ...acc,
        ...(stop.cargo_manifests || []).map(
          (manifest) => manifest && manifest.cargo_manifest_url
        ),
      ],
      []
    )
    .filter((v) => !!v);
}

export function destructureOrderParams(order) {
  if (!order) return {};

  const { journey_type: orderType, address_from, address_to, stops } = order;
  const { company_name: pickupCompany, label: pickupLabel } =
    address_from || {};
  const { company_name: dropoffCompany, label: dropoffLabel } =
    address_to || {};

  return {
    orderType,
    pickupCompany,
    pickupLabel,
    dropoffCompany,
    dropoffLabel,
    stops,
  };
}

export default enhance(ShipmentCard);
